// Administração do Ordo (só para administradores): pessoas, papéis, acesso a
// cada ambiente e o consumo da IA (Claude, Whisper e Workers AI) do app todo.

import * as store from "./store.js";
import * as router from "./router.js";
import { api } from "./api.js";
import { el, dialog, toast, confirmDialog, relTime, fmtDateTime, fmtDate, safeGet, safeSet } from "./dom.js";
import { iconNode, ui } from "./icons.js";
import { ROLES } from "./spaces.js";
import { avatar } from "./people.js";

let tab = safeGet("ordo.adminTab", "users");
let days = 30;

export async function renderAdmin(host) {
  if (store.state.me?.role !== "admin") {
    host.replaceChildren(el("div", { class: "list-view" }, el("p", {}, "Só administradores do Ordo veem esta tela.")));
    return;
  }
  const tabs = el("div", { class: "picker-tabs" });
  const body = el("div", { class: "admin-body" });
  host.replaceChildren(el("div", { class: "list-view wide" },
    el("h1", {}, ui("admin", 26), " Administração"),
    el("p", { class: "muted" }, "Pessoas com conta no Ordo, o que cada uma acessa e quanto a IA está custando."),
    tabs, body));
  const draw = async () => {
    tabs.replaceChildren(...[["users", "Pessoas e acessos"], ["sessions", "Sessões e logins"], ["published", "Páginas publicadas"], ["usage", "Consumo da IA"]].map(([id, l]) =>
      el("button", { class: "picker-tab" + (tab === id ? " on" : ""), onclick: () => { tab = id; safeSet("ordo.adminTab", id); draw(); } }, l)));
    body.replaceChildren(el("p", { class: "muted" }, "Carregando…"));
    try {
      if (tab === "usage") await drawUsage(body);
      else if (tab === "sessions") await drawSessions(body, draw);
      else if (tab === "published") await drawPublished(body, draw);
      else await drawUsers(body, draw);
    } catch (err) {
      body.replaceChildren(el("p", { class: "error" }, err.message));
    }
  };
  draw();
}

// ---------- Pessoas e acessos ----------

const usd = (v) => (v >= 100 ? `US$ ${v.toFixed(0)}` : v >= 1 ? `US$ ${v.toFixed(2)}` : v >= 0.01 || !v ? `US$ ${(v || 0).toFixed(3)}` : `US$ ${v.toFixed(5)}`);

async function drawUsers(body, redraw) {
  const data = await api("GET", "/manage/overview");
  const spaces = data.workspaces.filter((w) => !w.archived_at);
  const archived = data.workspaces.filter((w) => w.archived_at);
  const me = store.state.me;
  const roleSel = (u) => el("select", { class: "small", disabled: u.id === me.id, title: u.id === me.id ? "Você não pode tirar o seu próprio papel de administrador" : "", onchange: async (e) => {
    try { await api("PATCH", `/manage/users/${u.id}`, { role: e.target.value }); toast("Papel atualizado."); } catch (err) { toast(err.message); }
    redraw();
  } }, el("option", { value: "member", selected: u.role !== "admin" }, "Membro"), el("option", { value: "admin", selected: u.role === "admin" }, "Administrador"));

  const accessSel = (u, w) => {
    const cur = u.access.find((a) => a.workspace_id === w.id)?.role || "";
    return el("select", { class: "small access-sel" + (cur ? " has" : ""), disabled: u.role === "admin" || !!u.revoked_at,
      title: u.role === "admin" ? "Administradores do Ordo acessam todos os ambientes" : "",
      onchange: async (e) => {
        try {
          await api("PUT", `/manage/users/${u.id}/access`, { workspaces: [{ id: w.id, role: e.target.value || null }] });
          toast("Acesso atualizado.");
        } catch (err) { toast(err.message); }
        redraw();
      } },
    el("option", { value: "" }, u.role === "admin" ? "Tudo (admin)" : "Sem acesso"),
    ...ROLES.map(([v, l]) => el("option", { value: v, selected: v === cur }, l)));
  };

  const people = data.users.map((u) => el("tr", { class: u.revoked_at ? "blocked" : "" },
    el("td", {}, el("div", { class: "user-cell" }, avatar(u, "sm"),
      el("div", {}, el("strong", {}, u.name, u.id === me.id ? " (você)" : ""), el("div", { class: "muted small" }, u.email || "sem e-mail")))),
    el("td", {}, roleSel(u)),
    el("td", {}, u.revoked_at ? el("span", { class: "chip c-red" }, "Bloqueado") : el("span", { class: "chip c-green" }, "Ativo")),
    el("td", { class: "muted small" }, u.last_seen_at ? relTime(u.last_seen_at) : "nunca"),
    el("td", { class: "num" }, String(u.private_pages)),
    el("td", { class: "num" }, usd(u.ai_cost_30d)),
    el("td", {}, el("button", { class: "icon-btn tiny", title: "Mais", onclick: (e) => userMenu(u, e.currentTarget, redraw) }, ui("more", 14)))));

  const matrix = el("table", { class: "admin-table matrix" },
    el("thead", {}, el("tr", {}, el("th", {}, "Pessoa"), spaces.map((w) => el("th", {}, iconNode(w.icon, "🗂️"), " ", w.name)))),
    el("tbody", {}, data.users.filter((u) => !u.revoked_at).map((u) => el("tr", {},
      el("td", {}, u.name), spaces.map((w) => el("td", {}, accessSel(u, w)))))));

  body.replaceChildren(
    el("div", { class: "admin-actions" },
      el("button", { class: "primary", onclick: () => userDialog(null, spaces, redraw) }, ui("userPlus", 14), " Adicionar pessoa"),
      el("button", { class: "small", onclick: async () => (await import("./spaces.js")).createWorkspaceDialog() }, ui("space", 14), " Novo ambiente"),
      el("button", { class: "small", onclick: () => router.go({ view: "team" }) }, ui("org", 14), " Montar organograma")),
    el("h3", {}, "Pessoas"),
    el("div", { class: "table-wrap" }, el("table", { class: "admin-table" },
      el("thead", {}, el("tr", {}, ["Pessoa", "Papel no Ordo", "Situação", "Último acesso", "Páginas particulares", "IA (30 dias)", ""].map((h) => el("th", {}, h)))),
      el("tbody", {}, people))),
    el("h3", {}, "Acesso aos ambientes"),
    el("p", { class: "muted small" }, "Pode ver: lê e comenta. Pode editar: cria e edita páginas e tarefas. Administra: também gerencia membros. Administradores do Ordo acessam tudo."),
    spaces.length ? el("div", { class: "table-wrap" }, matrix) : el("p", { class: "muted" }, "Nenhum ambiente ainda. Crie um em \"Novo ambiente\"."),
    el("h3", {}, "Ambientes"),
    el("div", { class: "space-cards" }, data.workspaces.map((w) => el("div", { class: "space-card" + (w.archived_at ? " archived" : "") },
      el("div", { class: "space-card-head" }, iconNode(w.icon, "🗂️"), el("strong", {}, w.name)),
      el("div", { class: "muted small" }, `${w.members} membro(s) · ${w.pages} página(s)`, w.archived_at ? " · arquivado" : ""),
      el("div", { class: "row-actions" },
        w.archived_at
          ? el("button", { class: "small", onclick: async () => { await api("PATCH", `/workspaces/${w.id}`, { archived: false }); await store.loadTree(); redraw(); } }, "Restaurar")
          : el("button", { class: "small", onclick: async () => (await import("./spaces.js")).workspaceDialog(w.id, "members") }, "Membros"))))),
    archived.length ? el("p", { class: "muted small" }, `${archived.length} ambiente(s) arquivado(s) acima podem ser restaurados.`) : null);
}

// ---------- Sessões e logins (Better Auth, plugin admin) ----------

// "Windows · Ordo 0.6.0" a partir do user agent do Electron.
function deviceOf(ua = "") {
  const os = /Windows/i.test(ua) ? "Windows" : /Mac OS X|Macintosh/i.test(ua) ? "macOS" : /Android/i.test(ua) ? "Android"
    : /iPhone|iPad/i.test(ua) ? "iOS" : /Linux/i.test(ua) ? "Linux" : "";
  const app = /ordo\/([\d.]+)/i.exec(ua)?.[1];
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "";
  return [os, app ? `Ordo ${app}` : browser].filter(Boolean).join(" · ") || "Desconhecido";
}

const flag = (cc) => (/^[A-Z]{2}$/.test(cc || "") ? String.fromCodePoint(...[...cc].map((c) => 0x1f1a5 + c.charCodeAt(0))) + " " : "");
const placeOf = (s) => [s.city, s.region && s.region !== s.city ? s.region : null, s.country].filter(Boolean).join(", ");

async function drawSessions(body, redraw) {
  const { authClient, unwrap } = await import("./auth-client.js");
  const { apiToken } = await import("./api.js");
  const auth = authClient();
  const [{ users }, overview] = await Promise.all([
    unwrap(auth.admin.listUsers({ query: { limit: 500, sortBy: "name", sortDirection: "asc" } })),
    api("GET", "/manage/overview"),
  ]);
  const seen = Object.fromEntries(overview.users.map((u) => [u.id, u]));
  const lists = await Promise.all(users.map((u) => unwrap(auth.admin.listUserSessions({ userId: u.id })).then((r) => r.sessions || []).catch(() => [])));
  const now = Date.now();
  const rows = [];
  users.forEach((u, i) => {
    for (const s of lists[i]) {
      if (new Date(s.expiresAt).getTime() < now) continue;
      rows.push({ u, s });
    }
  });
  rows.sort((a, b) => new Date(b.s.updatedAt || b.s.createdAt) - new Date(a.s.updatedAt || a.s.createdAt));
  const mine = apiToken();
  const online = users.filter((u) => seen[u.id]?.last_seen_at && now - seen[u.id].last_seen_at < 5 * 60_000);

  const revoke = async (r) => {
    if (!(await confirmDialog(`Encerrar esta sessão de ${r.u.name}? A pessoa vai precisar entrar de novo nesse aparelho.`, { ok: "Encerrar", danger: true }))) return;
    try { await unwrap(auth.admin.revokeUserSession({ sessionToken: r.s.token })); toast("Sessão encerrada."); } catch (err) { toast(err.message); }
    redraw();
  };
  const revokeAll = async (u) => {
    if (!(await confirmDialog(`Encerrar todas as sessões de ${u.name}? Ela sai de todos os aparelhos.`, { ok: "Encerrar todas", danger: true }))) return;
    try { await unwrap(auth.admin.revokeUserSessions({ userId: u.id })); toast(`${u.name} vai precisar entrar de novo.`); } catch (err) { toast(err.message); }
    redraw();
  };

  body.replaceChildren(
    el("h3", {}, `Online agora (${online.length})`),
    online.length
      ? el("div", { class: "online-list" }, online.map((u) => el("span", { class: "chip c-green" }, avatar(seen[u.id], "sm"), " ", u.name)))
      : el("p", { class: "muted small" }, "Ninguém usou o Ordo nos últimos 5 minutos."),
    el("h3", {}, `Sessões ativas (${rows.length})`),
    el("p", { class: "muted small" }, "Cada login abre uma sessão por aparelho. O local é aproximado, calculado pela Cloudflare a partir do IP."),
    rows.length ? el("div", { class: "table-wrap" }, el("table", { class: "admin-table" },
      el("thead", {}, el("tr", {}, ["Pessoa", "Aparelho", "Local", "IP", "Entrou", "Último uso", ""].map((h) => el("th", {}, h)))),
      el("tbody", {}, rows.map((r) => el("tr", {},
        el("td", {}, el("div", { class: "user-cell" }, avatar(seen[r.u.id] || r.u, "sm"),
          el("div", {}, el("strong", {}, r.u.name), el("div", { class: "muted small" }, r.u.email)))),
        el("td", { title: r.s.userAgent || "" }, deviceOf(r.s.userAgent), r.s.token === mine ? el("span", { class: "chip c-blue small" }, " esta sessão") : null,
          r.s.impersonatedBy ? el("span", { class: "chip c-orange small" }, " acesso de admin") : null),
        el("td", {}, placeOf(r.s) ? flag(r.s.country) + placeOf(r.s) : el("span", { class: "muted" }, "—")),
        el("td", { class: "muted small" }, r.s.ipAddress || "—"),
        el("td", { class: "muted small", title: fmtDateTime(new Date(r.s.createdAt).getTime()) }, relTime(new Date(r.s.createdAt).getTime())),
        el("td", { class: "muted small" }, seen[r.u.id]?.last_seen_at ? relTime(seen[r.u.id].last_seen_at) : relTime(new Date(r.s.updatedAt).getTime())),
        el("td", {}, r.s.token === mine ? null : el("button", { class: "small", onclick: () => revoke(r) }, "Encerrar"))))))) : el("p", { class: "muted" }, "Nenhuma sessão ativa."),
    el("h3", {}, "Por pessoa"),
    el("div", { class: "table-wrap" }, el("table", { class: "admin-table" },
      el("thead", {}, el("tr", {}, ["Pessoa", "Sessões", "Último login", "Último acesso", ""].map((h) => el("th", {}, h)))),
      el("tbody", {}, users.map((u, i) => {
        const list = lists[i].filter((s) => new Date(s.expiresAt).getTime() >= now);
        const last = list.reduce((m, s) => Math.max(m, new Date(s.createdAt).getTime()), 0);
        return el("tr", { class: u.banned ? "blocked" : "" },
          el("td", {}, u.name, u.banned ? el("span", { class: "chip c-red small" }, " bloqueado") : null),
          el("td", { class: "num" }, String(list.length)),
          el("td", { class: "muted small" }, last ? relTime(last) : "—"),
          el("td", { class: "muted small" }, seen[u.id]?.last_seen_at ? relTime(seen[u.id].last_seen_at) : "nunca"),
          el("td", {}, list.length && u.id !== store.state.me.id ? el("button", { class: "small", onclick: () => revokeAll(u) }, "Encerrar todas") : null));
      })))));
}

function userMenu(u, anchor, redraw) {
  const me = store.state.me;
  import("./dom.js").then(({ menu }) => menu(anchor, [
    { label: "Editar nome, e-mail ou senha", icon: ui("userCog", 14), run: () => userDialog(u, [], redraw) },
    u.id !== me.id && { label: "Encerrar as sessões (sai de todos os PCs)", icon: ui("logout", 14), run: async () => {
      await api("POST", `/manage/users/${u.id}/logout`, {});
      toast(`${u.name} vai precisar entrar de novo.`);
    } },
    u.id !== me.id && "-",
    u.id !== me.id && { label: u.revoked_at ? "Desbloquear" : "Bloquear acesso", icon: ui(u.revoked_at ? "unlock" : "lock", 14), danger: !u.revoked_at, run: async () => {
      if (!u.revoked_at && !(await confirmDialog(`Bloquear ${u.name}? A pessoa sai na hora e não consegue mais entrar. As páginas dela continuam no Ordo.`, { ok: "Bloquear", danger: true }))) return;
      try { await api("PATCH", `/manage/users/${u.id}`, { blocked: !u.revoked_at }); } catch (err) { toast(err.message); }
      redraw();
    } },
  ], { align: "right" }));
}

function userDialog(u, spaces, redraw) {
  const name = el("input", { value: u?.name || "", placeholder: "Nome", required: true });
  const email = el("input", { type: "email", value: u?.email || "", placeholder: "email@empresa.com" });
  const pass = el("input", { type: "password", placeholder: u ? "Nova senha (deixe vazio para manter)" : "Senha inicial (mín. 8 caracteres)", autocomplete: "new-password" });
  const role = el("select", {}, el("option", { value: "member" }, "Membro"), el("option", { value: "admin" }, "Administrador"));
  const access = new Map();
  const spaceRows = (spaces || []).map((w) => {
    const sel = el("select", { class: "small" }, el("option", { value: "" }, "Sem acesso"), ROLES.map(([v, l]) => el("option", { value: v }, l)));
    sel.addEventListener("change", () => access.set(w.id, sel.value || null));
    return el("div", { class: "person-row" }, iconNode(w.icon, "🗂️"), el("span", { class: "grow" }, w.name), sel);
  });
  dialog((close) => [
    el("h3", {}, u ? `Editar ${u.name}` : "Adicionar pessoa"),
    !u ? el("p", { class: "muted small" }, "A conta é criada já com a senha que você definir. Passe o e-mail e a senha para a pessoa entrar no Ordo.") : null,
    el("label", { class: "field" }, "Nome", name),
    el("label", { class: "field" }, "E-mail", email),
    el("label", { class: "field" }, "Senha", pass),
    !u ? el("label", { class: "field" }, "Papel no Ordo", role) : null,
    !u && spaceRows.length ? el("h4", {}, "Ambientes") : null,
    ...(!u ? spaceRows : []),
    el("div", { class: "dlg-actions" }, el("button", { onclick: close }, "Cancelar"),
      el("button", { class: "primary", onclick: async () => {
        const bodyData = { name: name.value.trim(), email: email.value.trim() || undefined };
        if (pass.value) bodyData.password = pass.value;
        try {
          if (u) await api("PATCH", `/manage/users/${u.id}`, bodyData);
          else await api("POST", "/manage/users", { ...bodyData, role: role.value, workspaces: [...access].filter(([, r]) => r).map(([id, r]) => ({ id, role: r })) });
          close();
          toast(u ? "Conta atualizada." : "Pessoa adicionada.");
          store.loadTree().catch(() => {});
          redraw();
        } catch (err) { toast(err.message); }
      } }, u ? "Salvar" : "Criar conta")),
  ]);
  requestAnimationFrame(() => name.focus());
}

// ---------- Consumo da IA ----------

const PROVIDER = { anthropic: "Claude (Anthropic)", "workers-ai": "Workers AI (Cloudflare)", whisper: "Whisper (transcrição)", openai: "OpenAI (voz)", elevenlabs: "ElevenLabs (voz)" };
const FEATURE = {
  "resumo-reuniao": "Resumo de reunião", perguntar: "Perguntar ao Ordo", "extrair-tarefas": "Texto → tarefas", transcricao: "Transcrição",
  "perguntar-reunioes": "Perguntas sobre reuniões",
  "voz:perguntar": "Conversa por voz: respostas", "voz:transcricao": "Conversa por voz: sua fala", "voz:fala": "Conversa por voz: voz do Ordo",
};
const featureName = (f) => FEATURE[f] || (f?.startsWith("editor:") ? `Editor: ${f.slice(7).replace(/_/g, " ")}` : f || "outro");

// ---------- Páginas publicadas na internet ----------

async function drawPublished(body, redraw) {
  const { pages } = await api("GET", "/manage/published");
  if (!pages.length) {
    body.replaceChildren(el("p", { class: "muted" }, "Nenhuma página está publicada na internet. Quem é dono de uma página (ou administra o ambiente dela) publica em Compartilhar → Publicar na web."));
    return;
  }
  const unpublish = async (p) => {
    if (!(await confirmDialog(`Tirar "${p.title || "Sem título"}" da internet? O link público para de funcionar na hora.`, { ok: "Despublicar", danger: true }))) return;
    try { await api("DELETE", `/pages/${p.page_id}/publish`); toast("Página despublicada."); } catch (err) { toast(err.message); }
    redraw();
  };
  body.replaceChildren(
    el("p", { class: "muted small" }, "Tudo que qualquer pessoa consegue abrir sem login. Como administrador, você despublica qualquer uma."),
    el("div", { class: "table-wrap" }, el("table", { class: "admin-table" },
      el("tr", {}, ["Página", "Onde", "Publicada por", "Subpáginas", "Visitas", ""].map((h) => el("th", {}, h))),
      pages.map((p) => el("tr", {},
        el("td", {}, el("button", { class: "link", type: "button", title: "Abrir no Ordo", onclick: () => router.openPage(p.page_id) },
          iconNode(p.icon, p.kind === "database" ? "🗂️" : "📄"), " ", p.title || "Sem título"),
          p.archived ? el("span", { class: "muted small" }, " (na lixeira: o link não abre)") : null),
        el("td", { class: "muted" }, p.workspace ? p.workspace.name : `Particular de ${p.owner_name || "alguém"}`),
        el("td", {}, p.published_by?.name || "—", el("div", { class: "muted small" }, fmtDate(p.published_at))),
        el("td", {}, p.subpages ? "Incluídas" : "Só a página"),
        el("td", { class: "num" }, String(p.views || 0)),
        el("td", { class: "num" },
          el("button", { class: "small", type: "button", title: "Abrir no navegador", onclick: () => window.open(p.url, "_blank") }, ui("open", 13)), " ",
          el("button", { class: "small danger-btn", type: "button", onclick: () => unpublish(p) }, "Despublicar")))))));
}

async function drawUsage(body) {
  const u = await api("GET", `/manage/usage?days=${days}`);
  const total = u.totals.reduce((s, t) => s + (t.cost || 0), 0);
  const claude = u.totals.find((t) => t.provider === "anthropic") || {};
  const audio = u.totals.reduce((s, t) => s + (t.audio_seconds || 0), 0);
  const calls = u.totals.reduce((s, t) => s + (t.calls || 0), 0);
  const period = el("select", { onchange: (e) => { days = Number(e.target.value); body.replaceChildren(el("p", { class: "muted" }, "Carregando…")); drawUsage(body).catch((err) => body.replaceChildren(el("p", { class: "error" }, err.message))); } },
    [[7, "Últimos 7 dias"], [30, "Últimos 30 dias"], [90, "Últimos 90 dias"], [365, "Último ano"]].map(([v, l]) => el("option", { value: v, selected: v === days }, l)));
  const tile = (label, value, sub) => el("div", { class: "stat-tile" }, el("div", { class: "stat-label" }, label), el("div", { class: "stat-value" }, value), sub ? el("div", { class: "muted small" }, sub) : null);
  const table = (head, rows) => el("div", { class: "table-wrap" }, el("table", { class: "admin-table" },
    el("thead", {}, el("tr", {}, head.map((h, i) => el("th", { class: i && i >= head.length - 2 ? "num" : "" }, h)))),
    el("tbody", {}, rows.length ? rows.map((r) => el("tr", {}, r.map((c, i) => el("td", { class: i && i >= r.length - 2 ? "num" : "" }, c))))
      : el("tr", {}, el("td", { colspan: head.length, class: "muted" }, "Sem uso no período.")))));
  body.replaceChildren(
    el("div", { class: "admin-actions" }, period,
      el("span", { class: "muted small" }, u.claude_enabled ? `Modelo do Claude: ${u.model}` : "Claude desligado (falta a chave da API no servidor): a IA usa o Workers AI.")),
    el("div", { class: "stat-row" },
      tile("Custo total estimado", usd(total), `${calls} chamadas`),
      tile("Claude", usd(claude.cost || 0), `${((claude.input || 0) / 1000).toFixed(0)} mil tokens de entrada · ${((claude.output || 0) / 1000).toFixed(0)} mil de saída`),
      tile("Transcrição", `${Math.round(audio / 60)} min`, usd(u.totals.find((t) => t.provider === "whisper")?.cost || 0)),
      tile("Claude desde o início", usd(u.claude_all_time), u.tracking_since ? `medindo desde ${fmtDate(new Date(u.tracking_since).toISOString().slice(0, 10))}` : "ainda sem uso")),
    u.billing ? (u.billing.error
      ? el("p", { class: "muted small" }, `Fatura da Anthropic indisponível: ${u.billing.error}`)
      : el("div", { class: "billing" }, ui("check", 14), ` Cobrado pela Anthropic no período (fatura oficial): ${usd(u.billing.usd)}`))
      : el("p", { class: "muted small" }, "Valores estimados a partir dos tokens de cada chamada e do preço do modelo. Para ver também o valor cobrado de verdade, cadastre uma chave de Admin da Anthropic no servidor (ANTHROPIC_ADMIN_KEY)."),
    el("h3", {}, "Custo por dia"),
    dailyChart(u.byDay, days),
    el("h3", {}, "Por pessoa"),
    table(["Pessoa", "Serviço", "Chamadas", "Custo"], u.byUser.map((r) => [r.name || "Sistema (automático)", PROVIDER[r.provider] || r.provider, String(r.calls), usd(r.cost || 0)])),
    el("h3", {}, "Por recurso"),
    table(["Recurso", "Serviço", "Chamadas", "Custo"], u.byFeature.map((r) => [featureName(r.feature), PROVIDER[r.provider] || r.provider, String(r.calls), usd(r.cost || 0)])),
    el("h3", {}, "Por modelo"),
    table(["Modelo", "Serviço", "Chamadas", "Custo"], u.byModel.map((r) => [r.model || "-", PROVIDER[r.provider] || r.provider, String(r.calls), usd(r.cost || 0)])));
}

// Barras do custo diário (uma série: o total do dia; o detalhe aparece ao passar o mouse).
function dailyChart(byDay, nDays) {
  const map = new Map();
  for (const r of byDay) {
    const d = map.get(r.day) || { day: r.day, cost: 0, calls: 0, parts: [] };
    d.cost += r.cost || 0;
    d.calls += r.calls || 0;
    d.parts.push(r);
    map.set(r.day, d);
  }
  const daysList = [];
  const n = Math.min(nDays, 90);
  for (let i = n - 1; i >= 0; i--) {
    const day = new Date(Date.now() - 3 * 3600_000 - i * 86400_000).toISOString().slice(0, 10);
    daysList.push(map.get(day) || { day, cost: 0, calls: 0, parts: [] });
  }
  if (!daysList.some((d) => d.calls)) return el("p", { class: "muted" }, "Sem uso de IA no período.");
  const W = 760, H = 180, padL = 74, padB = 22, padT = 8;
  const max = Math.max(...daysList.map((d) => d.cost), 0.001);
  const nice = niceMax(max);
  const bw = (W - padL) / daysList.length;
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
  svg.setAttribute("class", "chart");
  svg.setAttribute("role", "img");
  svg.setAttribute("aria-label", "Custo da IA por dia");
  const add = (tag, attrs, text) => {
    const n2 = document.createElementNS(NS, tag);
    for (const [k, v] of Object.entries(attrs)) n2.setAttribute(k, v);
    if (text) n2.textContent = text;
    svg.append(n2);
    return n2;
  };
  for (const f of [0, 0.5, 1]) {
    const y = padT + (H - padT - padB) * (1 - f);
    add("line", { x1: padL, x2: W, y1: y, y2: y, class: "grid" });
    add("text", { x: padL - 6, y: y + 4, "text-anchor": "end", class: "axis" }, f ? usd(nice * f) : "0");
  }
  const tip = el("div", { class: "chart-tip", hidden: true });
  daysList.forEach((d, i) => {
    const h = (H - padT - padB) * (d.cost / nice);
    const x = padL + i * bw + Math.max(1, bw * 0.15);
    const w = Math.max(2, bw * 0.7);
    const y = H - padB - h;
    if (h > 0.5) {
      const r = Math.min(4, w / 2, h);
      add("path", { class: "bar", d: `M${x},${H - padB} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${H - padB} Z` });
    }
    const hit = add("rect", { x: padL + i * bw, y: padT, width: bw, height: H - padT - padB, class: "hit" });
    hit.addEventListener("mouseenter", () => {
      tip.hidden = false;
      tip.replaceChildren(el("strong", {}, fmtDate(d.day)), el("div", {}, `${usd(d.cost)} · ${d.calls} chamadas`),
        ...d.parts.map((p) => el("div", { class: "muted small" }, `${PROVIDER[p.provider] || p.provider}: ${usd(p.cost || 0)}`)));
      const left = ((padL + i * bw + bw / 2) / W) * 100;
      tip.style.left = `min(calc(100% - 200px), max(0px, calc(${left}% - 90px)))`;
    });
    hit.addEventListener("mouseleave", () => { tip.hidden = true; });
    if (daysList.length <= 31 ? i % Math.ceil(daysList.length / 8) === 0 : i % 15 === 0) {
      add("text", { x: padL + i * bw + bw / 2, y: H - 6, "text-anchor": "middle", class: "axis" }, d.day.slice(8, 10) + "/" + d.day.slice(5, 7));
    }
  });
  return el("div", { class: "chart-box" }, svg, tip);
}

function niceMax(v) {
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 2, 2.5, 5, 10]) if (v <= m * p) return m * p;
  return 10 * p;
}

export { fmtDateTime };
